import { Injectable, signal, effect, inject, Renderer2, RendererFactory2 } from '@angular/core';
import { AuthService } from './auth.service';
import { ApiService } from './api.service';
import { LocalStorageService } from './local-storage.service';
import { take } from 'rxjs/operators';
import { endpoints } from '../../../../environments/endpoints';

export type ColorMode = 'light' | 'dark';

@Injectable({
    providedIn: 'root'
})
export class ThemeService {
    private rendererFactory = inject(RendererFactory2);
    private renderer: Renderer2;
    private _authService = inject(AuthService);
    private _apiService = inject(ApiService);
    private _localStorageService = inject(LocalStorageService);
    private readonly THEME_KEY = 'preferred_theme';

    public mode = signal<ColorMode>('light');

    constructor() {
        this.renderer = this.rendererFactory.createRenderer(null, null);

        effect(() => {
            this.applyThemeToDom(this.mode());
        });
        this.initThemeSubscription();
    }

    public toggleMode(): void {
        const newMode = this.mode() === 'light' ? 'dark' : 'light';
        this.mode.set(newMode);
        this._localStorageService.setItem(this.THEME_KEY, newMode);
        this.updateUserThemePreference(newMode);
    }

    private initThemeSubscription(): void {
        this._authService.currentUser$.subscribe(user => {
            if (user?.preferred_theme) {
                this.mode.set(user.preferred_theme as ColorMode);
            } else {
                const storedTheme = this._localStorageService.getItem(this.THEME_KEY) as ColorMode | null;
                if (storedTheme && (storedTheme === 'light' || storedTheme === 'dark')) {
                    this.mode.set(storedTheme);
                } else {
                    this.mode.set('light');
                }
            }
        });
    }

    private applyThemeToDom(mode: ColorMode): void {
        const previousMode = mode === 'light' ? 'dark-theme' : 'light-theme';
        const currentMode = `${mode}-theme`;

        this.renderer.removeClass(document.body, previousMode);
        this.renderer.addClass(document.body, currentMode);
    }

    private updateUserThemePreference(mode: ColorMode): void {
        if (this._authService.isAuthenticated()) {
            const request = this._apiService.put(endpoints.api.users.updateMe, { body: { preferred_theme: mode }, skipLoading: true });
            this._apiService
                .run(request)
                .pipe(take(1))
                .subscribe();
        }
    }
}